/** Modal / slide-over displaying a metric's historical daily snapshots and export actions. */
import { useQuery } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { downloadTableExport, TableExportButton } from "@/shared/components/table-export-button";
import { EmptyState, ErrorState, LoadingState } from "@/shared/components/page";
import { Dialog } from "@/shared/ui/primitives";

export type MetricPoint = Schemas["MetricPointOut"];

/** Dialog showing the 90-day daily snapshot series for a metric with CSV/Excel export. */
export function MetricTrendDialog({
  metricKey,
  label,
  siteId,
  days = 90,
  onClose,
}: {
  metricKey: string;
  label: string;
  siteId?: string;
  days?: number;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const since = new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
  const until = new Date().toISOString().slice(0, 10);

  const series = useQuery({
    queryKey: ["metrics", "series", metricKey, siteId, since, until],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reporting/metrics/series", {
          params: {
            query: { key: metricKey, since, until, ...(siteId ? { site_id: siteId } : {}) },
          },
        }),
      ),
  });

  const points = (series.data ?? []).map((p) => ({
    date: p.date,
    value: p.value !== null ? Number(p.value) : 0,
  }));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} title={label}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            {since} → {until}
          </span>
          <TableExportButton
            onExport={(format) =>
              downloadTableExport(
                "/api/v1/reporting/metrics/series",
                `metric-${metricKey}`,
                { key: metricKey, since, until, site_id: siteId },
                format,
              )
            }
          />
        </div>

        {series.isPending ? (
          <LoadingState />
        ) : series.isError ? (
          <ErrorState error={series.error} onRetry={() => void series.refetch()} />
        ) : points.length === 0 ? (
          <EmptyState message={t("dashboard.noSnapshotsYet")} />
        ) : (
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={40} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#2563eb"
                  fill="#3b82f6"
                  fillOpacity={0.2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Dialog>
  );
}
